/**
 * useSeen — `true` once an element has come within `rootMargin` of the
 * viewport, and never `false` again. The ONE lazy-by-visibility primitive of
 * the public app: `MapBoundary` gates the Maps SDK on it, and `EstateGallery`
 * gates its billed per-view Google photo fetch on it (D14). One latch, so the
 * two cannot come to disagree about what "in view" means.
 *
 * `enabled: false` observes nothing. An element that is not rendered
 * (`display: none`) never intersects, so it is never seen. Where
 * `IntersectionObserver` does not exist the element counts as seen at once: a
 * browser without it is a browser we cannot be lazy in, and a missing surface
 * is worse than an eager one. The server renders `false` (effects do not run
 * during SSR), so hydration cannot flip it.
 */
import { useEffect, useState } from 'react';

export function useSeen(el: Element | null, enabled: boolean, rootMargin: string): boolean {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!enabled || seen || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, enabled, seen, rootMargin]);
  return seen;
}
